import { useEffect, useState } from "react";
import { PublicConfig } from "@/types";

const API = process.env.REACT_APP_BACKEND_URL || "";
const STORAGE_KEY = "stagestudios:public-config:v1";

// Cloudflare's "always passes" test site key. Used as the absolute fallback
// when the backend hasn't been configured yet. Visible to users as a red
// "For testing only" badge — which is the cue that the admin still needs
// to enter a real site key in /admin/system.
export const TURNSTILE_TEST_SITE_KEY = "1x00000000000000000000AA";

let inflight: Promise<PublicConfig> | null = null;
let cached: PublicConfig | null = null;

/**
 * Lazy fetch of non-secret system config. Cached in-memory for the lifetime
 * of the SPA, and a copy is mirrored to localStorage so the very first paint
 * (before the network call resolves) can still render the right values.
 */
async function fetchPublicConfig(): Promise<PublicConfig> {
  // Always attempt a fresh network read — the config carries security-relevant
  // flags (captcha_disabled, turnstile site key) that must NOT be served stale
  // from a prior session's cache. The in-memory `cached`/localStorage copies
  // are used ONLY as an offline fallback when the network call fails.
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const r = await fetch(`${API}/api/public-config`, { credentials: "include", cache: "no-store" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data: PublicConfig = await r.json();
      cached = data;
      try { window.localStorage?.setItem(STORAGE_KEY, JSON.stringify(data)); } catch (_) { /* ignore */ }
      return data;
    } catch (_) {
      // Network failure — fall back to localStorage cache from a prior load.
      try {
        const raw = window.localStorage?.getItem(STORAGE_KEY);
        if (raw) { cached = JSON.parse(raw); return cached as PublicConfig; }
      } catch (_) { /* ignore */ }
      return {} as PublicConfig;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/**
 * React hook — returns `{config, loading}`. `config` is the raw JSON shape
 * returned by /api/public-config: `{turnstile: {site_key}, gemini: {...}}`.
 *
 * On first mount, returns `{}` while the request is in flight. Components
 * should treat missing values as "not configured" and fall back to a
 * sensible default (e.g. the Turnstile test key).
 */
export function usePublicConfig(): { config: PublicConfig; loading: boolean } {
  const [config, setConfig] = useState<PublicConfig>(() => {
    if (cached) return cached;
    try {
      const raw = window.localStorage?.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (_) { return {}; }
  });
  const [loading, setLoading] = useState<boolean>(!cached);

  useEffect(() => {
    let cancelled = false;
    fetchPublicConfig().then((data) => {
      if (cancelled) return;
      setConfig(data || {});
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  return { config, loading };
}

/** Convenience: returns the configured Turnstile site key.
 *
 * SECURITY: in production we FAIL CLOSED — if the admin hasn't configured a
 * real Turnstile widget in /admin/system, return `null` and let the caller
 * disable the form. In development we fall back to Cloudflare's always-pass
 * test key so local dev doesn't require a real widget.
 *
 * Returning `null` here is the explicit "do not render the captcha widget"
 * signal — CaptchaWidget handles it by showing an admin-misconfigured banner
 * and the submit button stays disabled.
 */
export function useTurnstileSiteKey(): string | null {
  const { config } = usePublicConfig();
  const key = config?.turnstile?.site_key;
  if (key && key.trim()) return key.trim();
  // Dev fallback only — Cloudflare's "always passes" key. Gated on NODE_ENV so
  // it can never leak into a production build.
  if (process.env.NODE_ENV !== "production") {
    return TURNSTILE_TEST_SITE_KEY;
  }
  return null;
}

/**
 * Pre-launch flag (env-gated, surfaced via /api/public-config). True when the
 * backend has AUTH_DISABLE_CAPTCHA=true — the CaptchaWidget auto-passes instead
 * of mounting the live Turnstile widget.
 */
export function useCaptchaDisabled(): boolean {
  const { config } = usePublicConfig();
  return config?.captcha_disabled === true;
}

/**
 * Pre-launch flag — true when the backend has APP_TEST_MODE=true. Drives the
 * site-wide "TEST MODE" banner.
 */
export function useTestMode(): boolean {
  const { config } = usePublicConfig();
  return config?.test_mode === true;
}
